import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { useSelector } from "react-redux";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import CustomDropdown from "../../../custom/customDropdown";
import { AuthContext } from "../../../Header/auth - context";
import { uzmiGresku } from "../../../custom/greske";
import useVisinaTabele from "../../../custom/visinaTabele";
import "../Fakture.css";

/* ═══════════════════════════════════════════════════════════════════════════
   Pregled ZAVEDENIH ulaznih faktura (invoice.Faktura, tip 304).

   Ovo NIJE „Provera SEF". Tamo stoji ono što je stiglo sa SEF-a i čeka odluku;
   ovde su fakture koje su već unete u knjigu — uključujući one unete ručno, koje
   kroz SEF nikad nisu ni prošle.

   Do ovog ekrana zavedena ulazna faktura nije imala nijedan spisak: videla se
   samo kao stavka u izveštajima i analitičkoj kartici.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Datum ide kroz toUtcIso — bez korekcije offseta toISOString ume da pomeri
   datum za dan unazad. Isti obrazac kao na susednim ekranima. */
const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

const pocetakMeseca = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
};

/* Iznosi se prikazuju sa dve decimale i razdvojenim hiljadama — isto kao u
   ostatku modula. Prazno umesto nule kad vrednosti nema, da kolona ne bude
   zid nula. */
const fmtIznos = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDatum = (v) => {
    if (!v) return "";
    const s = String(v).substring(0, 10);
    const [g, m, d] = s.split("-");
    return d ? `${d}.${m}.${g}.` : s;
};

const PregledUlaznih = ({ naOtvori }) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector((state) => state.company);
    const toast = useRef(null);

    const [datumOd, setDatumOd] = useState(pocetakMeseca());
    const [datumDo, setDatumDo] = useState(new Date());
    const [klijent, setKlijent] = useState(null);
    /* Jedno polje pretrage preko SVEGA po čemu se faktura prepoznaje — naš broj,
       broj dobavljača, naziv, PIB, referenca, podpozicija, mesto, napomena.
       Operater ne zna unapred šta drži u ruci, pa pet odvojenih polja samo usporava. */
    const [pretraga, setPretraga] = useState("");
    /* null = sve | 1 = otvorene | 0 = zatvorene | 2 = delimično plaćene */
    const [stanje, setStanje] = useState(null);
    const [statusUlazne, setStatusUlazne] = useState(null);
    const [valuta, setValuta] = useState(null);
    /* null = svejedno | 1 = dospele a otvorene | 0 = nedospele */
    const [dospelo, setDospelo] = useState(null);
    /* null = svejedno | 1 = stigle kroz SEF | 0 = unete ručno */
    const [izSefa, setIzSefa] = useState(null);

    const [podaci, setPodaci] = useState([]);
    const [dobavljaci, setDobavljaci] = useState([]);
    const [statusi, setStatusi] = useState([]);
    const [valute, setValute] = useState([]);
    const [ucitava, setUcitava] = useState(false);
    /* Brzo suzavanje VEĆ učitane stranice. Odvojeno od polja „Pretraga" iznad,
       koje ide u bazu i gleda celu knjigu — ovo filtrira samo ono što je na ekranu. */
    const [filterTabele, setFilterTabele] = useState(null);
    /* Spisak se zavrsava na dnu ekrana; visina se MERI, ne pogadja. */
    const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([podaci, filterTabele], 160);

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 5000 });

    const posalji = async (step, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/ulazne", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify({ step, ...telo }),
        });
        const tekst = await r.text();
        const d = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(d.message || t("common.greska"));
        return d;
    };

    const greska = async (e) => {
        const g = await uzmiGresku(e, t("common.greska"));
        poruka("error", t("common.greska"), g.message);
    };

    /* Spisak dobavljača dolazi iz INIT-a iste procedure — isti šifarnik koji
       koristi i forma za unos, pa se ne mogu razići. */
    useEffect(() => {
        posalji("INIT")
            .then((d) => {
                setDobavljaci(d?.DobavljaciDB || []);
                /* Statusi i valute dolaze iz iste procedure — front ih NE hardkoduje,
                   da se spisak ne raziđe sa model_gen-om pri prvoj dopuni. */
                setStatusi(d?.StatusiDB || []);
                setValute(d?.ValuteDB || []);
            })
            .catch(() => { /* šifarnik nije uslov za pregled — filteri samo ostanu prazni */ });
        // eslint-disable-next-line
    }, []);

    const ucitaj = () => {
        setUcitava(true);
        posalji("GET_PREGLED", {
            datumOd: toUtcIso(datumOd),
            datumDo: toUtcIso(datumDo),
            id_klijent: klijent,
            pretraga: pretraga || null,
            stanje,
            statusUlazne,
            valuta,
            dospelo,
            izSefa,
        })
            .then((d) => setPodaci(Array.isArray(d) ? d : (d?.recordset || [])))
            .catch(greska)
            .finally(() => setUcitava(false));
    };

    useEffect(() => { ucitaj(); /* eslint-disable-next-line */ }, []);

    const ponistiFiltere = () => {
        setDatumOd(pocetakMeseca());
        setDatumDo(new Date());
        setKlijent(null);
        setPretraga("");
        setStanje(null);
        setStatusUlazne(null);
        setValuta(null);
        setDospelo(null);
        setIzSefa(null);
    };

    /* Zbirovi stoje u podnožju tabele: direktor skoro uvek prvo pogleda koliko
       je ukupno otvoreno, pa tek onda pojedinačne fakture. */
    const zbir = useMemo(() => (podaci || []).reduce((a, r) => ({
        bruto:    a.bruto    + (Number(r.Bruto)    || 0),
        placeno:  a.placeno  + (Number(r.Placeno)  || 0),
        otvoreno: a.otvoreno + (Number(r.Otvoreno) || 0),
    }), { bruto: 0, placeno: 0, otvoreno: 0 }), [podaci]);

    const stanjePilula = (r) => {
        const n = String(r.StanjePlacanja || "").toLowerCase();
        const klasa = n.startsWith("zatvor") ? "sef-st-ok"
                    : n.startsWith("delim")  ? "sef-st-upozorenje"
                    :                          "sef-st-kritican";
        return <span className={`sef-pilula ${klasa}`}>{r.StanjePlacanja}</span>;
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Ulazne fakture");
        const headers = [
            t("ulazne.brojFakture"), t("sefUlazne.broj"), t("ulazne.dobavljac"), t("ulazne.podpozicija"), t("sefUlazne.pib"),
            t("nova.datum"), t("ulaznePregled.dpu"), t("nova.rokPlacanjaDatum"),
            t("ulazne.ukupno"), t("ulaznePregled.placeno"), t("ulaznePregled.otvoreno"),
            t("ulazne.valuta"), t("ulaznePregled.stanje"),
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        (podaci || []).forEach((r) => {
            const row = worksheet.addRow([
                r.Broj, r.BrojDobavljaca, r.Dobavljac, r.Podpozicija || "", r.PIB,
                fmtDatum(r.Datum), fmtDatum(r.DPU), fmtDatum(r.RokPlacanja),
                Number(r.Bruto) || 0, Number(r.Placeno) || 0, Number(r.Otvoreno) || 0,
                r.Valuta, r.StanjePlacanja,
            ]);
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade",
        }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `${t("ulaznePregled.naslov")} - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const zaglavljeTabele = (
        <div className="flex align-items-center gap-2 export-buttons" style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: "0.4rem" }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                        onClick={exportExcel} data-pr-tooltip="XLS" />
            </div>
            <input type="search" placeholder={t("common.pretraga")} className="p-inputtext p-component"
                   onInput={(e) => setFilterTabele(e.target.value)} />
        </div>
    );

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <div className="faktureStranica">
                <Card>
                    {/* Isti raspored kao na „Provera SEF" — `sef-ulazne-filter` je
                        flex red koji se prelama, a `sef-filter-polje` drži labelu
                        iznad kontrole. Prva verzija je koristila klasu koju sam
                        izmislio i koja nigde nije postojala, pa je forma ispala
                        neporavnata. Dugmad idu u `sef-filter-dugmad`, koji ih gura
                        na desnu ivicu reda. */}
                    <div className="sef-ulazne-filter">
                        <div className="sef-filter-polje">
                            <label htmlFor="od">{t("sefUlazne.periodOd")}:</label>
                            <Calendar id="od" value={datumOd} onChange={(e) => setDatumOd(e.value)}
                                      dateFormat="dd.mm.yy." showIcon mask="99.99.9999" />
                        </div>
                        <div className="sef-filter-polje">
                            <label htmlFor="do">{t("sefUlazne.periodDo")}:</label>
                            <Calendar id="do" value={datumDo} onChange={(e) => setDatumDo(e.value)}
                                      dateFormat="dd.mm.yy." showIcon mask="99.99.9999" />
                        </div>
                        <div className="sef-filter-polje sef-filter-siroko">
                            <label htmlFor="dob">{t("ulazne.dobavljac")}:</label>
                            <CustomDropdown id="dob" value={klijent} options={dobavljaci} optionLabel="label"
                                            filter showClear placeholder={t("sefUlazne.sviDobavljaci")}
                                            style={{ width: "100%" }}
                                            onChange={(e) => setKlijent(e.target.value)} />
                        </div>
                        <div className="sef-filter-polje sef-filter-siroko">
                            <label htmlFor="pr">{t("ulaznePregled.pretraga")}:</label>
                            <InputText id="pr" value={pretraga} onChange={(e) => setPretraga(e.target.value)}
                                       placeholder={t("ulaznePregled.pretragaOpis")}
                                       onKeyDown={(e) => { if (e.key === "Enter") ucitaj(); }} />
                        </div>
                        <div className="sef-filter-polje">
                            <label htmlFor="st">{t("ulaznePregled.stanje")}:</label>
                            <CustomDropdown id="st" value={stanje} optionLabel="label"
                                            showClear placeholder={t("ulaznePregled.sve")}
                                            style={{ width: "100%" }}
                                            options={[
                                                { value: 1, label: t("ulaznePregled.samoOtvorene") },
                                                { value: 2, label: t("ulaznePregled.samoDelimicno") },
                                                { value: 0, label: t("ulaznePregled.samoZatvorene") },
                                            ]}
                                            onChange={(e) => setStanje(e.target.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label htmlFor="su">{t("ulaznePregled.statusUlazne")}:</label>
                            <CustomDropdown id="su" value={statusUlazne} options={statusi} optionLabel="label"
                                            showClear placeholder={t("ulaznePregled.sviStatusi")}
                                            style={{ width: "100%" }}
                                            onChange={(e) => setStatusUlazne(e.target.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label htmlFor="val">{t("ulazne.valuta")}:</label>
                            <CustomDropdown id="val" value={valuta} options={valute} optionLabel="label"
                                            showClear placeholder={t("ulaznePregled.sveValute")}
                                            style={{ width: "100%" }}
                                            onChange={(e) => setValuta(e.target.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label htmlFor="dos">{t("ulaznePregled.dospelost")}:</label>
                            <CustomDropdown id="dos" value={dospelo} optionLabel="label"
                                            showClear placeholder={t("ulaznePregled.sve")}
                                            style={{ width: "100%" }}
                                            options={[
                                                { value: 1, label: t("ulaznePregled.dospele") },
                                                { value: 0, label: t("ulaznePregled.nedospele") },
                                            ]}
                                            onChange={(e) => setDospelo(e.target.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label htmlFor="sef">{t("ulaznePregled.poreklo")}:</label>
                            <CustomDropdown id="sef" value={izSefa} optionLabel="label"
                                            showClear placeholder={t("ulaznePregled.sve")}
                                            style={{ width: "100%" }}
                                            options={[
                                                { value: 1, label: t("ulaznePregled.izSefa") },
                                                { value: 0, label: t("ulaznePregled.rucno") },
                                            ]}
                                            onChange={(e) => setIzSefa(e.target.value)} />
                        </div>
                        <div className="sef-filter-dugmad">
                            {/* Sa osam filtera je vraćanje na početno stanje čest potez —
                                bez dugmeta bi se svaki morao ručno prazniti. */}
                            <Button type="button" label={t("ulaznePregled.ponisti")} icon="pi pi-filter-slash"
                                    className="p-button-secondary" onClick={ponistiFiltere} />
                            <Button type="button" label={t("sefUlazne.ucitaj")} icon="pi pi-search"
                                    loading={ucitava} onClick={ucitaj} />
                        </div>
                    </div>
                </Card>

                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <div ref={omotTabele}>
                    <DataTable
                        value={podaci || []}
                        paginator rows={redovaTabele} rowsPerPageOptions={[redovaTabele, 50, 100]}
                        globalFilter={filterTabele}
                        scrollable scrollHeight={visinaTabele}
                        header={zaglavljeTabele}
                        resizableColumns columnResizeMode="expand"
                        loading={ucitava}
                        emptyMessage={t("common.noData")}
                        className="turePregledTable">
                        {naOtvori && <Column header={t("ulaznePregled.otvori")} style={{ minWidth: "100px" }}
                                body={(r) => <Button label={t("ulaznePregled.otvori")} icon="pi pi-pencil" type="button"
                                    className="p-button-text p-button-sm" onClick={() => naOtvori(r)} />} />}
                        <Column field="Broj"           header={t("ulazne.brojFakture")}           sortable style={{ minWidth: "90px" }} />
                        <Column field="BrojDobavljaca" header={t("sefUlazne.broj")}        sortable style={{ minWidth: "150px" }} />
                        <Column field="Dobavljac"      header={t("ulazne.dobavljac")}      sortable style={{ minWidth: "220px" }} />
                        <Column field="Podpozicija"    header={t("ulazne.podpozicija")}    sortable style={{ minWidth: "120px" }} />
                        <Column field="PIB"            header={t("sefUlazne.pib")}         sortable style={{ minWidth: "110px" }} />
                        <Column field="Datum"          header={t("nova.datum")}            sortable style={{ minWidth: "110px" }}
                                body={(r) => fmtDatum(r.Datum)} />
                        <Column field="RokPlacanja"    header={t("nova.rokPlacanjaDatum")} sortable style={{ minWidth: "120px" }}
                                body={(r) => fmtDatum(r.RokPlacanja)} />
                        <Column field="Bruto"          header={t("ulazne.ukupno")}         sortable style={{ minWidth: "120px" }}
                                body={(r) => <span className="ad-viol-num">{fmtIznos(r.Bruto)}</span>} />
                        <Column field="Placeno"        header={t("ulaznePregled.placeno")}  sortable style={{ minWidth: "120px" }}
                                body={(r) => <span className="ad-viol-num">{fmtIznos(r.Placeno)}</span>} />
                        <Column field="Otvoreno"       header={t("ulaznePregled.otvoreno")} sortable style={{ minWidth: "120px" }}
                                body={(r) => <span className="ad-viol-num">{fmtIznos(r.Otvoreno)}</span>} />
                        <Column field="Valuta"         header={t("ulazne.valuta")}         sortable style={{ minWidth: "90px" }} />
                        <Column field="StanjePlacanja" header={t("ulaznePregled.stanje")}  sortable style={{ minWidth: "150px" }}
                                body={stanjePilula} />
                        <Column field="StatusUlazneNaziv" header={t("ulaznePregled.statusUlazne")} sortable style={{ minWidth: "150px" }}
                                body={(r) => r.StatusUlazneNaziv || "—"} />
                        {/* Veza sa SEF sandučetom: prazno znači da faktura nije došla
                            kroz SEF nego je uneta ručno — to je ispravno stanje. */}
                        <Column field="SefOdluka"      header={t("ulaznePregled.sef")}     sortable style={{ minWidth: "150px" }}
                                body={(r) => r.id_sef_dokument ? (r.SefOdluka || r.SefStatus || "—") : "—"} />
                    </DataTable>
                    </div>

                    <div className="ulazne-zbir">
                        <span>{t("ulaznePregled.zbirUkupno")}: <b>{fmtIznos(zbir.bruto)}</b></span>
                        <span>{t("ulaznePregled.placeno")}: <b>{fmtIznos(zbir.placeno)}</b></span>
                        <span>{t("ulaznePregled.otvoreno")}: <b>{fmtIznos(zbir.otvoreno)}</b></span>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default PregledUlaznih;
